import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import {
  appUrlRoot,
  CustomerTiers,
  dashboardUrl,
  DOMAIN_NAME,
  forgotPasswordUrl,
  loginUrl,
  loginWithCredentialsUrl,
  loginWithSsoUrl,
  opencloudHomeUrl,
  setCookieUrl,
} from 'app/constants'
import { LoginMethodTypes } from 'app/plugins/account/components/userManagement/users/helpers'
import { authenticateUser } from 'app/plugins/account/components/userManagement/users/new-actions'
import Alert from 'core/components/Alert'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import ExternalLink from 'core/components/ExternalLink'
import { MessageTypes } from 'core/components/notifications/model'
import SimpleLink from 'core/components/SimpleLink'
import { loadFeatures } from 'core/containers/AppController'
import FormPageContainer from 'core/containers/FormPageContainer'
import Button from 'core/elements/button'
import Text from 'core/elements/Text'
import { useToast } from 'core/providers/ToastProvider'
import { PreferencesState, preferencesStoreKey } from 'core/session/preferencesReducers'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import {
  getAppBasePath,
  getProductName,
  getSSORedirectUrl,
  isDefaultDomain,
  isDevelopment,
  isOpenCloud,
} from 'core/utils/helpers'
import Cookies from 'js-cookie'
import moment from 'moment'
import { isEmpty, prop } from 'ramda'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import Bugsnag from 'utils/bugsnag'
import generateTestId from 'utils/test-helpers'
import { trackEvent } from 'utils/tracking'
import Progress from 'core/components/progress/Progress'
import CredentialLoginForm from './CredentialLoginForm'
import SSOLoginForm from './SSOLoginForm'

const useStyles = makeStyles((theme: Theme) => ({
  methodToggle: {
    position: 'absolute',
    top: 17,
    right: 26,
    cursor: 'pointer',

    '&:hover': {
      textDecoration: 'none',
    },
  },
  form: {
    maxWidth: 400,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 24,
  },
  paragraph: {
    textAlign: 'center',
  },
  signinButton: {
    marginTop: 16,
    width: '100%',
  },
  formTitle: {
    textAlign: 'center',
  },
  fields: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'start',
    gap: 16,
  },
  progress: {
    '& img': {
      height: 130,
    },
    '& img ~ span': {
      fontSize: theme.typography.subtitle1.fontSize,
    },
  },
}))

interface Props {
  onAuthSuccess: any
  ssoEnabled: boolean
  customerTier: string
  infraHostUrl: string
}

const defaultParams = {
  username: '',
  password: '',
  domain: 'default',
  loginMethod: LoginMethodTypes.Local,
  MFAcheckbox: false,
  mfa: '',
  loginFailed: false,
  loading: false,
  ssoEnabled: true,
}

// Determine login method from route pathname
const getLoginMethodFromRoute = (pathname: string, ssoEnabled: boolean): LoginMethodTypes => {
  if (pathname === loginUrl) {
    return LoginMethodTypes.SSO
  }
  if (pathname === loginWithCredentialsUrl) {
    return LoginMethodTypes.Local
  }
  return ssoEnabled ? LoginMethodTypes.SSO : LoginMethodTypes.Local
}

const LoginPage: React.FC<Props> = (props) => {
  const classes = useStyles({})
  const showToast = useToast()

  const { onAuthSuccess, customerTier, infraHostUrl /*, ssoEnabled */ } = props
  const ssoEnabled = true
  const { history, location } = useReactRouter()
  const { getUserPrefs } = useScopedPreferences()

  // Derive login method from URL pathname - single source of truth
  const currentLoginMethod = useMemo(() => getLoginMethodFromRoute(location.pathname, ssoEnabled), [
    location.pathname,
    ssoEnabled,
  ])

  const [params, setParams] = useState(defaultParams)
  const [showPassword, setShowPassword] = useState(false)
  const [openForgetPassModal, setOpenForgetPassModal] = useState(false)
  const preferences = useSelector(prop<string, PreferencesState>(preferencesStoreKey))
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features, username } = session
  const intendedDestination = location.state?.from
  const appBasePath = getAppBasePath()
  const hasIntendedDestination =
    !!intendedDestination &&
    !['/', appBasePath, appUrlRoot, loginUrl, `${appUrlRoot}/index.html`].includes(
      intendedDestination,
    )
  const shouldAutoRedirectToSsoLogin = isOpenCloud() && hasIntendedDestination && !isDevelopment()

  const updateParams = (p) => {
    setParams((params) => {
      return { ...params, ...p }
    })
  }
  const {
    username: loginUsername,
    password,
    MFAcheckbox,
    mfa,
    loginFailed,
    loading,
    domain,
  } = params

  useEffect(() => {
    // Check if features are loaded in the session
    if (isEmpty(features)) {
      const getFeatures = async () => {
        await loadFeatures()
      }

      getFeatures()
    }
  }, [location.pathname, ssoEnabled])

  const setDomainNameCookie = (domain: string) => {
    Cookies.remove(DOMAIN_NAME) // Clear any existing cookie
    Cookies.set(DOMAIN_NAME, domain) // Set new cookie
  }

  // Redirect to SSO login
  const redirectToSsoLogin = () => {
    const appBasePath = getAppBasePath()
    const ssoRedirectUrl = getSSORedirectUrl(domain)
    const originHost = window.location.origin
    if (originHost === infraHostUrl) {
      if (!isDefaultDomain(domain)) setDomainNameCookie(domain)
      window.location.href = `${appBasePath}${ssoRedirectUrl}`
    } else {
      const url = new URL(
        `${infraHostUrl}${appBasePath}${setCookieUrl}?redirectUrl=${originHost}${appBasePath}${loginWithSsoUrl}&domain=${domain}`,
      )
      window.location.replace(url)
    }
  }

  const updateValue = (key) => (event) => {
    updateParams({ [key]: event.target.value })
  }

  const handleLoginError = () => {
    // If SSO login fails, user is redirected so don't show the alert
    if (currentLoginMethod === LoginMethodTypes.SSO) {
      return
    }
    return updateParams({ loading: false, loginFailed: true })
  }

  const performLogin = useCallback(
    async (event) => {
      event.preventDefault()
      updateParams({ loginFailed: false, loading: true })
      const isDefaultDomainName = isDefaultDomain(domain)

      // If user enters a domain other than defaul, set it in the cookie
      if (!isDefaultDomainName) {
        setDomainNameCookie(domain)
      }

      Bugsnag.leaveBreadcrumb('Attempting PF9 Sign In', {
        loginUsername,
        loginMethod: currentLoginMethod,
        MFAcheckbox,
        mfa,
      })

      const { username, unscopedToken, expiresAt, issuedAt, isSsoToken } = await authenticateUser({
        loginUsername,
        password,
        loginMethod: currentLoginMethod,
        MFAcheckbox,
        mfa,
        ...(!isDefaultDomainName && { domain }),
      })

      const lastVisitedPath = preferences?.[username]?.root?.lastVisitedPath

      // If login fails, show error message
      if (!unscopedToken) {
        return handleLoginError()
      }

      // Track successful login
      trackEvent('PF9 Signed In', {
        username,
        duDomain: window.location.origin,
      })
      const { currentTenant, currentRegion } = getUserPrefs(username)

      // Trigger authentication success logic
      await onAuthSuccess({
        currentTenant,
        currentRegion,
        username,
        password,
        unscopedToken,
        expiresAt,
        issuedAt,
        isSsoToken,
        ...(!isDefaultDomainName && { domain }),
      })

      updateParams({ loading: false })

      const defaultUrl = isOpenCloud() ? opencloudHomeUrl : dashboardUrl
      // Use intended destination if available, otherwise fall back to lastVisitedPath or default URL
      const redirectPath = intendedDestination || lastVisitedPath || defaultUrl
      history.push(redirectPath)
    },
    [
      onAuthSuccess,
      loginUsername,
      password,
      currentLoginMethod,
      MFAcheckbox,
      mfa,
      domain,
      preferences,
      intendedDestination,
      getUserPrefs,
      history,
    ],
  )

  const handleChangeBox = (name) => (checked) => {
    updateParams({ [name]: checked })
  }

  // Common condition to determine if we're in an airgapped environment
  const isAirgappedEnvironment = features?.experimental?.airgapped

  const handleForgotPassword = useCallback(
    () => (e) => {
      if (!features?.experimental) {
        showToast('Still loading, please try refreshing the page.', MessageTypes.warning, 3000, {
          vertical: 'top',
          horizontal: 'center',
        })
        return
      }
      isAirgappedEnvironment ? setOpenForgetPassModal(true) : history.push(forgotPasswordUrl)
    },
    [features, showToast, isAirgappedEnvironment],
  )

  useEffect(() => {
    const params = new URLSearchParams(history.location.search)
    const ssoLoginError = params.get('ssoLoginError')

    if (ssoLoginError === 'true') {
      showToast(
        `This account is not authorized to access ${getProductName()}. For more information please contact your admin.`,
        MessageTypes.error,
        5000,
        {
          vertical: 'top',
          horizontal: 'center',
        },
      )
    }
  }, [showToast])

  useEffect(() => {
    // Store the intended destination in session storage if SSO is selected
    // This is used to redirect the user to the intended destination after SSO login
    if (currentLoginMethod === LoginMethodTypes.SSO) {
      sessionStorage.setItem('targetPath', intendedDestination)
    } else {
      sessionStorage.removeItem('targetPath')
    }
  }, [currentLoginMethod, intendedDestination])

  useEffect(() => {
    if (shouldAutoRedirectToSsoLogin && infraHostUrl) {
      redirectToSsoLogin()
    }
  }, [shouldAutoRedirectToSsoLogin, infraHostUrl])

  if (shouldAutoRedirectToSsoLogin) {
    return (
      <div className={classes.progress}>
        <Progress loading message="Redirecting to SSO login" />
      </div>
    )
  }

  return (
    <>
      {features?.experimental?.airgapped && (
        <ConfirmationDialog
          open={openForgetPassModal}
          title="Forgot your password?"
          text="Please contact your administrator to reset the password."
          cancelText="Close"
          customFooterActions={
            <Button
              variant="secondary"
              onClick={() => {
                setOpenForgetPassModal(false)
              }}
            >
              Close
            </Button>
          }
        />
      )}
      <FormPageContainer
        footer={
          customerTier !== CustomerTiers.OEM && !isOpenCloud() ? (
            <Text className={classes.paragraph} variant="caption3" color="textSecondary">
              By signing in, you agree to our{' '}
              <ExternalLink url="https://platform9.com/terms-conditions/" textVariant="caption3">
                Terms of Service
              </ExternalLink>
              . © 2014-{moment().year()} Platform9 Systems, Inc.
            </Text>
          ) : null
        }
      >
        <form
          data-testid={generateTestId('login', 'form')}
          className={classes.form}
          onSubmit={performLogin}
        >
          {ssoEnabled && (
            <SimpleLink
              data-testid={generateTestId('login', 'local', 'credentials')}
              className={classes.methodToggle}
              textVariant="caption1"
              src={
                location.pathname === loginWithCredentialsUrl ? loginUrl : loginWithCredentialsUrl
              }
            >
              {location.pathname === loginWithCredentialsUrl ? 'Use SSO' : 'Use local credentials'}
            </SimpleLink>
          )}
          <Text variant="h3" className={classes.formTitle}>
            Sign In
          </Text>
          <div className={classes.fields}>
            {currentLoginMethod === LoginMethodTypes.Local && (
              <CredentialLoginForm
                username={loginUsername}
                password={password}
                domain={domain}
                MFAcheckbox={MFAcheckbox}
                mfa={mfa}
                showPassword={showPassword}
                loginFailed={loginFailed}
                shouldRedirect={!isAirgappedEnvironment}
                forgotPasswordUrl={forgotPasswordUrl}
                updateValue={updateValue}
                setShowPassword={setShowPassword}
                handleChangeBox={handleChangeBox}
                handleForgotPassword={handleForgotPassword}
              />
            )}
            {loginFailed && (
              <Alert
                id="login-failed"
                variant="error"
                title="Login Failed."
                message={
                  currentLoginMethod === LoginMethodTypes.Local
                    ? 'Please check your username, password, domain and try again'
                    : ''
                }
              />
            )}
            {currentLoginMethod === LoginMethodTypes.SSO ? (
              <SSOLoginForm
                domain={domain}
                loading={loading}
                updateValue={updateValue}
                redirectToSsoLogin={redirectToSsoLogin}
              />
            ) : (
              <Button
                id="login-submit"
                type="submit"
                loading={loading}
                className={classes.signinButton}
              >
                {loading ? 'Attempting login...' : 'Sign In'}
              </Button>
            )}
          </div>
        </form>
      </FormPageContainer>
    </>
  )
}

export default LoginPage
